import { Button, Card, MonoLabel } from '@/design-system';
import { formatWaiting, type AwaitingCompletion } from '@/entities/order';
import { CardTitleRow } from '@/shared/ui';

import { dashboardCopy } from '../copy';

export interface AwaitingCompletionCardProps {
    rows: readonly AwaitingCompletion[];
    autoSend: boolean | null;
    onOpen: (row: AwaitingCompletion) => void;
    onOpenAll: () => void;
}

/**
 * "Wacht op afronding": adres, wachttijd in mono en één knop. Een rij zonder
 * vervolgactie hoort hier niet (principe 7).
 */
export function AwaitingCompletionCard({
    rows,
    autoSend,
    onOpen,
    onOpenAll,
}: AwaitingCompletionCardProps) {
    return (
        <Card clip>
            <CardTitleRow
                title={dashboardCopy.awaitingTitle}
                action={
                    <Button variant="ghost" onClick={onOpenAll}>
                        {dashboardCopy.awaitingAll}
                    </Button>
                }
            />

            {rows.length === 0 ? (
                <p className="m-0 px-4 py-[13px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {dashboardCopy.awaitingEmpty}
                </p>
            ) : (
                rows.map((row) => (
                    <div
                        key={row.orderUuid}
                        className="grid grid-cols-[1fr_auto_auto] items-center gap-[14px] border-b border-[var(--border-subtle-2)] px-4 py-[13px]"
                    >
                        <div className="min-w-0">
                            <div className="truncate text-[14px] font-medium text-[var(--text-primary)]">
                                {row.address}
                            </div>
                            <div className="mt-[3px] text-[12px] text-[var(--text-tertiary-safe)]">
                                {row.meta}
                            </div>
                        </div>
                        <MonoLabel
                            size={11}
                            variant="value"
                            tone="default"
                            aria-label={dashboardCopy.waitingLabel(row.address)}
                        >
                            {formatWaiting(row.waitingHours)}
                        </MonoLabel>
                        <Button variant="outline" size="xs" onClick={() => onOpen(row)}>
                            {dashboardCopy.awaitingAction}
                        </Button>
                    </div>
                ))
            )}

            {autoSend === null ? null : (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-[14px]">
                    <span className="text-[13px] text-[var(--text-tertiary-safe)]">
                        {autoSend ? dashboardCopy.autoSendNote : dashboardCopy.autoSendOffNote}
                    </span>
                    <MonoLabel size={11} tone={autoSend ? 'accent' : 'muted'}>
                        {autoSend ? dashboardCopy.autoSendOn : dashboardCopy.autoSendOff}
                    </MonoLabel>
                </div>
            )}
        </Card>
    );
}
